import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Clock, Boxes, Bell, PackageOpen, AlertTriangle } from "lucide-react";
import { ContainerCard } from "../components/ContainerCard";
import { HowItWorks } from "../components/HowItWorks";
import { AlertsSheet } from "../components/AlertsSheet";
import { RisksOverlay } from "../components/RisksOverlay";
import { BoxesOverlay } from "../components/BoxesOverlay";
import { SpoilSoonOverlay } from "../components/SpoilSoonOverlay";
import { HouseholdSwitcherSheet } from "../components/HouseholdSwitcherSheet";
import { HouseholdSwitchTransition } from "../components/HouseholdSwitchTransition";
import { Avatar } from "../components/Avatar";
import { FamilyAvatarBubbles } from "../components/FamilyAvatarBubbles";
import { containersApi, statsApi, notificationsApi, householdsApi } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useFilters, filtersToParams } from "../lib/FilterContext";
import { markAlertsViewed, pruneViewedAlertIds, unreadAlertCount } from "../lib/viewedAlerts";
import { getHouseholdIcon } from "../lib/householdIcons";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const Dashboard = () => {
  const { user, updateMe } = useAuth();
  const { filters } = useFilters();
  const [containers, setContainers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourOpen, setTourOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [feed, setFeed] = useState({ enabled: true, alerts: [] });
  const [feedLoading, setFeedLoading] = useState(false);
  // Re-render trigger when the user marks all alerts as viewed — bumps a
  // dummy counter that flushes the badge computation against localStorage.
  const [viewedTick, setViewedTick] = useState(0);
  const [risksOpen, setRisksOpen] = useState(false);
  const [boxesOpen, setBoxesOpen] = useState(false);
  const [spoilSoonOpen, setSpoilSoonOpen] = useState(false);
  const [householdSwitcherOpen, setHouseholdSwitcherOpen] = useState(false);
  const [activeHousehold, setActiveHousehold] = useState(null);
  // While true, the full-screen transition overlay is shown and the user
  // can't interact with stale data. We hold it up for at least the duration
  // of the animation (~1.4s) so it never flashes by, even if the API + load
  // both come back faster than the animation.
  const [switchingTo, setSwitchingTo] = useState(null); // { from, to } | null

  // Pull the active household name so we can surface it on the home page.
  // Lightweight call (single doc); silently no-ops for users without a household.
  useEffect(() => {
    if (!user?.household_id) {
      setActiveHousehold(null);
      return;
    }
    householdsApi
      .me()
      .then((h) => setActiveHousehold(h))
      .catch(() => setActiveHousehold(null));
  }, [user?.household_id]);

  // Auto-show tour ONLY the first time per user (backend-tracked)
  useEffect(() => {
    if (user && user.tour_seen === false) {
      const t = setTimeout(() => setTourOpen(true), 500);
      return () => clearTimeout(t);
    }
  }, [user]);

  const closeTour = async () => {
    setTourOpen(false);
    if (user && !user.tour_seen) {
      try { await updateMe({ tour_seen: true }); }
      catch (err) { console.warn("Failed to persist tour_seen:", err); }
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        containersApi.list({ ...filtersToParams(filters), sort: "expiring_soon" }),
        statsApi.get(),
      ]);
      setContainers(list);
      setStats(s);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const f = await notificationsApi.feed();
      setFeed(f);
      // Garbage-collect stale viewed IDs so old alerts that come back later
      // re-badge properly. Safe to call every refresh — it's a tiny O(n) op.
      pruneViewedAlertIds(user?.id, (f.alerts || []).map((a) => a.id));
    } catch (err) {
      console.warn("Failed to load notifications feed:", err);
    } finally {
      setFeedLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFeed(); }, [loadFeed, user?.notification_prefs]);

  const firstName = user?.name?.split(" ")[0] || "Friend";

  return (
    <div className="px-5 pt-6 pb-4">
      {/* Brand */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex items-start justify-between gap-3"
        data-testid="brand-block"
      >
        <div>
          <h1 className="font-heading text-4xl font-extrabold brand-gradient-text tracking-tight leading-none">
            QFresh
          </h1>
          <p className="text-sm text-[#8A93A6] mt-1 tracking-wide">Smart Storage</p>
        </div>
        <button
          onClick={() => setAlertsOpen(true)}
          data-testid="dashboard-notifications-btn"
          aria-label="Open alerts"
          className="relative w-11 h-11 rounded-full bg-white border border-[#E2E0D8] flex items-center justify-center text-[#1F5566] hover:bg-[#F5F3ED] transition-colors shadow-sm"
        >
          <Bell className="w-5 h-5" />
          {feed.enabled && (() => {
            // Compute unread separately from `feed.alerts.length` so that
            // "mark all as viewed" can clear the badge without deleting
            // the underlying alerts. `viewedTick` is the dependency that
            // forces a re-read of the localStorage viewed set.
            void viewedTick;
            const unread = unreadAlertCount(user?.id, feed.alerts);
            if (unread === 0) return null;
            return (
              <span
                data-testid="alerts-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E07A5F] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            );
          })()}
          {!feed.enabled && (
            <span
              data-testid="notif-disabled-dot"
              className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#8A93A6] border border-white"
            />
          )}
        </button>
      </motion.div>

      {/* Greeting */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
        className="mt-7"
        data-testid="greeting"
      >
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-2xl font-semibold text-[#2D3142]">
              {greeting()}, <span className="text-[#277A82]">{firstName}</span>
            </h2>
            <p className="text-[#4F5D75] mt-1 text-sm">Here's what's in your fridge.</p>
          </div>
          {/* Family avatar cluster — Apple-Messages style. Shows the current
              user as the big centered bubble (still a button → /profile) and
              up to 4 other household members floating around it. Falls back
              to just the centered avatar if the user is the only member of
              their household. */}
          {user && (
            <FamilyAvatarBubbles
              currentUser={user}
              members={activeHousehold?.members || []}
              to="/profile"
              testid="dashboard-profile-btn"
            />
          )}
        </div>

        {/* Active-household chip: a tappable shortcut to /household that
            keeps the user oriented when they're a member of more than one.
            Hidden if the user has no household at all. */}
        {activeHousehold && (() => {
          const multi = (user?.household_ids?.length || 0) > 1;
          const ActiveIcon = getHouseholdIcon(activeHousehold.icon);
          // Shared visual content between the two variants below.
          const inner = (
            <>
              <ActiveIcon className="w-3.5 h-3.5 shrink-0" />
              <span className="text-[11px] uppercase tracking-[0.14em] font-bold opacity-70">Active</span>
              <span className="text-xs font-semibold truncate max-w-[200px]">
                {activeHousehold.name}
              </span>
              {multi && (
                <span className="text-[10px] font-bold bg-white rounded-full px-1.5 py-0.5">
                  {user.household_ids.length}
                </span>
              )}
            </>
          );
          // Multi-household users get a switcher sheet here so they can hop
          // between fridges without going to /household first. Single-household
          // users still navigate to the household page (no switcher to show).
          return multi ? (
            <button
              type="button"
              onClick={() => setHouseholdSwitcherOpen(true)}
              data-testid="dashboard-active-household"
              className="mt-3 inline-flex items-center gap-1.5 max-w-full px-3 py-1.5 rounded-full bg-[#DCEDF0] border border-[#277A82]/40 text-[#1F5566] hover:bg-[#C4DFE3] transition-colors"
            >
              {inner}
            </button>
          ) : (
            <Link
              to="/household"
              data-testid="dashboard-active-household"
              className="mt-3 inline-flex items-center gap-1.5 max-w-full px-3 py-1.5 rounded-full bg-[#DCEDF0] border border-[#277A82]/40 text-[#1F5566] hover:bg-[#C4DFE3] transition-colors"
            >
              {inner}
            </Link>
          );
        })()}
      </motion.div>

      {/* Three stat boxes */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="mt-6 grid grid-cols-3 gap-2.5"
        data-testid="stats-row"
      >
        <div className="tup-card p-0 relative overflow-visible" data-testid="stat-expiring-soon">
          {stats && stats.expired > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setRisksOpen(true);
              }}
              data-testid="stat-risk-chip"
              title="Tap to review past best-by items"
              className="absolute -top-1.5 -right-1.5 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#FBE2DA] border border-[#D95D39]/40 hover:bg-[#F9D2C5] hover:border-[#D95D39]/70 transition-colors active:scale-95 shadow-sm"
            >
              <AlertTriangle
                className="w-3 h-3 text-[#8C2A0F] caution-anim"
                strokeWidth={2.6}
              />
              <span className="text-[10px] font-bold text-[#8C2A0F] leading-none">
                {stats.expired} Risk{stats.expired === 1 ? "" : "s"}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setSpoilSoonOpen(true)}
            data-testid="stat-expiring-soon-btn"
            className="w-full text-left p-3 rounded-2xl hover:bg-[#F5F3ED] transition-colors active:scale-[0.98]"
          >
            <div className="w-8 h-8 rounded-lg bg-[#FBEAE5] flex items-center justify-center shrink-0">
              <Clock className="w-4 h-4 text-[#B04A2A]" />
            </div>
            <div
              className="font-heading text-2xl font-bold text-[#2D3142] mt-2.5 leading-none"
              data-testid="stat-expiring-soon-value"
            >
              {stats ? stats.expiring_soon : "—"}
            </div>
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-[#8A93A6] mt-1.5 leading-tight">
              Likely to<br />Spoil Soon
            </div>
          </button>
        </div>

        <button
          type="button"
          onClick={() => setBoxesOpen(true)}
          data-testid="stat-total"
          className="tup-card p-3 text-left hover:bg-[#F5F3ED] transition-colors active:scale-[0.98]"
        >
          <div className="w-8 h-8 rounded-lg bg-[#DCEDF0] flex items-center justify-center">
            <Boxes className="w-4 h-4 text-[#1F5566]" />
          </div>
          <div className="font-heading text-2xl font-bold text-[#2D3142] mt-2.5 leading-none">
            {stats ? stats.active : "—"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-[#8A93A6] mt-1.5 leading-tight">
            Total Active
          </div>
        </button>

        <button
          type="button"
          onClick={() => setBoxesOpen(true)}
          data-testid="stat-unused"
          className="tup-card p-3 text-left hover:bg-[#F5F3ED] transition-colors active:scale-[0.98]"
        >
          <div className="w-8 h-8 rounded-lg bg-[#E8F0E5] flex items-center justify-center">
            <PackageOpen className="w-4 h-4 text-[#4A8A78]" />
          </div>
          <div className="font-heading text-2xl font-bold text-[#2D3142] mt-2.5 leading-none">
            {stats ? stats.unused : "—"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-[#8A93A6] mt-1.5 leading-tight">
            Unused
          </div>
        </button>
      </motion.div>

      {/* Add CTA */}
      <Link
        to="/add"
        data-testid="add-cta"
        className="mt-4 flex items-center justify-center gap-2 w-full bg-[#2D3142] hover:bg-[#1f2230] text-white rounded-xl py-3 font-semibold text-sm transition-colors"
      >
        <Plus className="w-4 h-4" /> Log a container
      </Link>

      {/* Containers */}
      <div className="mt-7" data-testid="container-list">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold text-[#2D3142]">
            By urgency
          </h3>
          <span className="text-xs text-[#8A93A6]">{loading ? "…" : `${containers.length}`}</span>
        </div>

        <div className="space-y-3">
          {containers.map((c) => (
            <ContainerCard key={c.id} container={c} />
          ))}
          {!loading && containers.length === 0 && (
            <div className="tup-card p-8 text-center" data-testid="empty-state">
              <div className="font-heading text-lg font-semibold text-[#2D3142]">
                Nothing in your fridge yet
              </div>
              <p className="text-sm text-[#4F5D75] mt-2">
                Tap the + button or the camera tab to add your first container.
              </p>
            </div>
          )}
        </div>
      </div>

      <HowItWorks open={tourOpen} onClose={closeTour} />
      <AlertsSheet
        open={alertsOpen}
        onClose={() => setAlertsOpen(false)}
        alerts={feed.alerts}
        enabled={feed.enabled}
        loading={feedLoading}
        onAlertsChange={(next) => setFeed((f) => ({ ...f, alerts: next }))}
        onMarkAllViewed={(ids) => {
          // Persist the IDs in localStorage and bump the tick so the bell
          // badge immediately drops to zero without a refetch.
          markAlertsViewed(user?.id, ids);
          setViewedTick((t) => t + 1);
        }}
      />
      <RisksOverlay
        open={risksOpen}
        onClose={() => setRisksOpen(false)}
        onChange={() => { load(); loadFeed(); }}
      />
      <BoxesOverlay
        open={boxesOpen}
        onClose={() => setBoxesOpen(false)}
        onChange={() => { load(); loadFeed(); }}
      />
      <SpoilSoonOverlay
        open={spoilSoonOpen}
        onClose={() => setSpoilSoonOpen(false)}
      />
      <HouseholdSwitcherSheet
        open={householdSwitcherOpen}
        onClose={() => setHouseholdSwitcherOpen(false)}
        activeHouseholdId={user?.household_id}
        onSwitched={async (newHousehold) => {
          // 1. Immediately drop the sheet (so the user sees the
          //    transition over the full page, not over the sheet).
          setHouseholdSwitcherOpen(false);
          const fromName = activeHousehold?.name || null;
          const toName = newHousehold?.name || null;
          const fromIcon = activeHousehold?.icon || "home";
          const toIcon = newHousehold?.icon || "home";
          setSwitchingTo({ from: fromName, to: toName, fromIcon, toIcon });
          // 2. Optimistically swap the displayed active household so the
          //    chip + family bubbles reflect the new home as the overlay
          //    closes. Re-fetched below for accuracy.
          setActiveHousehold(newHousehold);
          // 3. Re-fetch everything in parallel with the animation. We
          //    require BOTH the data refresh AND a minimum hold time
          //    (1400ms) before dismissing the overlay — that way the
          //    animation always completes, even if the network is fast.
          const minHold = new Promise((res) => setTimeout(res, 1400));
          const refresh = (async () => {
            try {
              const fresh = await householdsApi.me();
              setActiveHousehold(fresh);
            } catch (e) {
              console.warn("Failed to refresh household post-switch:", e);
            }
            await Promise.all([load(), loadFeed()]);
          })();
          await Promise.all([minHold, refresh]);
          setSwitchingTo(null);
        }}
      />
      <HouseholdSwitchTransition
        open={!!switchingTo}
        fromName={switchingTo?.from}
        toName={switchingTo?.to}
        fromIcon={switchingTo?.fromIcon}
        toIcon={switchingTo?.toIcon}
      />
    </div>
  );
};

export default Dashboard;
